import { ApiRequestError } from "@nexxt/api-client";
import { Button, toast } from "@nexxt/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type LinkOptions, useNavigate } from "@tanstack/react-router";
import { BellOff, BellRing } from "lucide-react";
import { useEffect } from "react";
import { errorText } from "../errors";
import { m } from "../paraglide/messages.js";
import type { BellItem } from "../shell/bell-item";
import { type PushClient, type PushState, readPushState, turnPushOff, turnPushOn } from "./push-device";

// The pushes of this device in the bell (ADR-283): turned on and off by the person, here and nowhere else, and the
// page a notification opens. Nothing shows where the environment has push off.

const settingsKey = ["push", "settings"] as const;
const stateKey = ["push", "state"] as const;

const hints: Partial<Record<PushState, () => string>> = {
  unsupported: m.push_unsupported,
  install: m.push_install,
  denied: m.push_denied,
};

/** "On this device": the state of this browser and the one button that changes it. */
export function PushSection({ client }: { client: PushClient }) {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: settingsKey, queryFn: () => client.settings(), staleTime: Infinity });
  const publicKey = settings.data?.publicKey ?? null;
  const state = useQuery({
    queryKey: [...stateKey, publicKey],
    queryFn: () => readPushState(publicKey ?? "", client),
    enabled: publicKey !== null,
  });
  const change = useMutation({
    mutationFn: async (on: boolean): Promise<PushState> => {
      if (!on) {
        await turnPushOff(client);
        return "off";
      }
      const outcome = await turnPushOn(client);
      return outcome === "on" ? "on" : outcome === "denied" ? "denied" : "off";
    },
    onSuccess: (now) => {
      queryClient.setQueryData([...stateKey, publicKey], now);
      if (now === "on") toast.success(m.push_on_done());
      else if (now === "off") toast.success(m.push_off_done());
    },
    onError: (error) => toast.error(error instanceof ApiRequestError ? errorText(error) : m.push_failed()),
  });
  if (publicKey === null || !state.data) return null;
  const now = state.data;
  const hint = hints[now];
  return (
    <section aria-labelledby="push-title" className="grid gap-2 border-t border-border px-4 py-3 text-sm">
      <h2 id="push-title" className="font-medium">
        {m.push_title()}
      </h2>
      {hint ? (
        <p className="text-muted-foreground">{hint()}</p>
      ) : (
        <>
          <p className="text-muted-foreground">{now === "on" ? m.push_on_hint() : m.push_off_hint()}</p>
          <Button
            variant="secondary"
            size="sm"
            className="justify-self-start"
            disabled={change.isPending}
            onClick={() => change.mutate(now !== "on")}
          >
            {now === "on" ? <BellOff aria-hidden /> : <BellRing aria-hidden />}
            {now === "on" ? m.push_turn_off() : m.push_turn_on()}
          </Button>
        </>
      )}
    </section>
  );
}

/** What a push says it is about, from the service worker. */
type PushedItem = Pick<BellItem, "id" | "templateCode" | "entityType" | "entityId">;

const OPEN_MESSAGE = "nexxt:push-open";
const HASH = "#push=";

function pushedItem(value: unknown): PushedItem | null {
  const item = value as Partial<PushedItem> | null;
  if (typeof item !== "object" || item === null || typeof item.id !== "string") return null;
  if (typeof item.templateCode !== "string") return null;
  const idOrNull = (v: unknown) => (typeof v === "string" ? v : null);
  return {
    id: item.id,
    templateCode: item.templateCode,
    entityType: idOrNull(item.entityType),
    entityId: idOrNull(item.entityId),
  };
}

/**
 * A click on a notification opens the page of its row, as the bell does, and marks it as read: told by the service
 * worker to a tab already open, or read once from the address of a tab it opened (`#push=`), which then forgets it.
 */
export function usePushOpen(
  client: {
    markRead(input: { ids: string[] }): Promise<unknown>;
    linkOf(item: PushedItem): LinkOptions | null;
  },
  bellKey: readonly string[],
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(() => {
    const open = (item: PushedItem | null) => {
      if (!item) return;
      void client.markRead({ ids: [item.id] }).then(
        () => queryClient.invalidateQueries({ queryKey: bellKey }),
        () => undefined,
      );
      const link = client.linkOf(item);
      if (link) void navigate(link);
    };
    if (location.hash.startsWith(HASH)) {
      let item: PushedItem | null = null;
      try {
        item = pushedItem(JSON.parse(decodeURIComponent(location.hash.slice(HASH.length))));
      } catch {
        item = null;
      }
      history.replaceState(history.state, "", location.pathname + location.search);
      open(item);
    }
    if (!("serviceWorker" in navigator)) return;
    const heard = (event: MessageEvent) => {
      const data = event.data as { type?: string; item?: unknown } | null;
      if (data?.type === OPEN_MESSAGE) open(pushedItem(data.item));
    };
    navigator.serviceWorker.addEventListener("message", heard);
    return () => navigator.serviceWorker.removeEventListener("message", heard);
  }, [client, navigate, queryClient, bellKey]);
}
